import type { FC } from 'react';
import type { useEvaultAddon } from 'hooks';

import { useCallback, useState } from 'react';

import { Confirm, Switch, fmtDate } from 'views/design-system';

import { money } from './derive';

/**
 * The same control as the billing row: on accrues $4.99 to this month's invoice,
 * off ends the add-on at the cycle boundary. `onChanged` runs after a successful
 * change so the drive can re-mint its token and re-probe entitlement.
 *
 * Takes the `useEvaultAddon()` result as a PROP rather than calling the hook
 * itself. `/evault` renders this component twice at once (the head, and again
 * inside the unentitled `Blank`) — two independent hook instances would mean
 * two independent `useState`s, so switching on through one leaves the other
 * still reading the old `status` until a full remount. The page calls the hook
 * once and passes the same object to every instance it renders.
 */
export const EvaultAddonToggle: FC<{
	addon: ReturnType<typeof useEvaultAddon>;
	onChanged: () => Promise<void>;
	canWrite: boolean;
}> = ({ addon, onChanged, canWrite }) => {
	const { status, isLoaded, isPurchasing, purchase, cancel } = addon;
	const [confirm, setConfirm] = useState<'on' | 'off' | null>(null);

	const handle = useCallback(
		async () => {
			if (!confirm) return;
			const ok = confirm === 'on' ? await purchase() : await cancel();
			if (ok) {
				setConfirm(null);
				await onChanged();
			}
		},
		[confirm, purchase, cancel, onChanged]
	);

	if (!isLoaded || !status) return null;
	const on = status.active && !status.endsAt;
	const blocked = !canWrite
		? 'You need billing write access to change the add-on.'
		: !status.available
		? 'Subscribe to a plan first — the add-on attaches to your existing subscription.'
		: '';
	/* Same field, same formatter as the billing row's own off-dialog
	   (`views/design-system/format`'s `fmtDate`, shared rather than reimplemented): endsAt
	   once a switch-off is pending, else the open cycle's end — so the two
	   surfaces never quote different dates for the same cycle. */
	const ends = status.endsAt ?? status.cycleEnd;

	return (
		<span className="addon-toggle">
			<span className="addon-toggle__lbl">
				eVault add-on{status.endsAt ? ` · ends ${fmtDate(status.endsAt)}` : ''}
			</span>
			<Switch
				checked={on}
				disabled={!!blocked || isPurchasing}
				label="eVault add-on"
				title={blocked || undefined}
				onChange={next => setConfirm(next ? 'on' : 'off')}
			/>
			<Confirm
				open={confirm !== null}
				title={confirm === 'off' ? 'Switch eVault off?' : 'Add eVault to your plan?'}
				body={
					confirm === 'off'
						? `eVault stays on until ${
								ends ? fmtDate(ends) : 'the end of this billing cycle'
						  }. After that, vaults cannot be used until you switch it back on. Nothing is deleted.`
						: `eVault will be added to your plan at $${money(status.price)}/month, billed with your monthly invoice, plus $${money(status.transferPrice)} per transferred document.`
				}
				label={confirm === 'off' ? 'Switch off' : 'Add to plan'}
				onConfirm={handle}
				onClose={() => setConfirm(null)}
				loading={isPurchasing}
			/>
		</span>
	);
};
